import {
  Box,
  Button,
  Checkbox,
  HStack,
  Icon,
  Input,
  InputGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { LuSearch } from 'react-icons/lu';

import Modal from '@/components/ui/Modal/Modal';
import { TAssignedCustomers } from '@/types/agents';
import { ICustomer } from '@/types/customers';

interface AssignCustomersModalProps {
  isOpen: boolean;
  onClose: () => void;
  customers: ICustomer[];
  assignedCustomers: TAssignedCustomers[];
  onSave: (selectedCustomerIds: string[]) => void;
  isLoading?: boolean;
}

const AssignCustomersModal = ({
  isOpen,
  onClose,
  customers,
  assignedCustomers,
  onSave,
  isLoading = false,
}: AssignCustomersModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      const assignedIds = new Set(assignedCustomers.map((c) => c.customerId));
      setSelectedCustomerIds(assignedIds);
      setSearchQuery('');
    }
  }, [isOpen, assignedCustomers]);

  const handleToggleCustomer = (customerId: string) => {
    setSelectedCustomerIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        newSet.add(customerId);
      }

      return newSet;
    });
  };

  const handleSave = () => {
    onSave(Array.from(selectedCustomerIds));
  };

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign agent to customers"
      maxWidth="600px"
      footer={
        <HStack gap={3} justifyContent="flex-end" width="100%" pt={4}>
          <Button
            variant="outline"
            onClick={onClose}
            px={4}
            py={2}
            fontSize={14}
            fontWeight={500}
            borderColor="gray.200"
            color="fg"
            _hover={{
              backgroundColor: 'gray.50',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            colorPalette="purple"
            backgroundColor="purple.600"
            color="white"
            onClick={handleSave}
            px={4}
            py={2}
            fontSize={14}
            fontWeight={500}
            borderRadius={6}
            loading={isLoading}
            loadingText="Saving..."
            _hover={{
              backgroundColor: 'purple.700',
            }}
          >
            Save Assignments
          </Button>
        </HStack>
      }
    >
      <VStack align="stretch" gap={4}>
        <Text fontSize={14} color="fg.muted" mb={2}>
          Only assigned customers will see this agent in their chatbot builder.
        </Text>
        <InputGroup
          startElement={
            <Box pl={3}>
              <Icon as={LuSearch} color="gray.400" boxSize={4} />
            </Box>
          }
        >
          <Input
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="4px"
            px={3}
            py={2}
            fontSize={14}
            _focus={{
              borderColor: 'purple.500',
              boxShadow: '0 0 0 1px purple.500',
            }}
            _hover={{
              borderColor: 'gray.300',
            }}
          />
        </InputGroup>
        <Box maxHeight="400px" overflowY="auto">
          <VStack align="stretch" gap={1}>
            {filteredCustomers.length === 0 ? (
              <Text fontSize={14} color="fg.muted" p={3} textAlign="center">
                No customers found
              </Text>
            ) : (
              filteredCustomers.map((customer) => {
                const isChecked = selectedCustomerIds.has(customer.referenceId);

                return (
                  <HStack
                    key={customer.referenceId}
                    gap={3}
                    p={2}
                    borderRadius="4px"
                    _hover={{ backgroundColor: 'gray.50' }}
                    cursor="pointer"
                    onClick={() => handleToggleCustomer(customer.referenceId)}
                  >
                    <Checkbox.Root checked={isChecked} cursor="pointer">
                      <Checkbox.HiddenInput />
                      <Checkbox.Control
                        borderColor="black"
                        _checked={{
                          backgroundColor: 'black',
                          borderColor: 'black',
                        }}
                      >
                        <Checkbox.Indicator color="white" />
                      </Checkbox.Control>
                      <Checkbox.Label fontSize={14} fontWeight={400} color="fg">
                        {customer.name}
                      </Checkbox.Label>
                    </Checkbox.Root>
                  </HStack>
                );
              })
            )}
          </VStack>
        </Box>
      </VStack>
    </Modal>
  );
};

export default AssignCustomersModal;